import React, { useContext, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, Popover, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import BanUserConfirm from 'src/apps/Account/components/BanUserConfirm'
import UserListItem from 'src/components/Settings/UserListItem'
import { useOrganization, useUserList, useRemoveUserFromRole, useResendRoleParticipationEmail }
   from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'

const RoleUserList = ({
   roleId,
   searchValue,
   status,
   targetId,
   targetType
}) => {
   const match = useRouteMatch()
   const [modalIsOpen, setModalIsOpen] = useState(false)
   const [popoverConfig, setPopoverConfig] = useState({
      anchorElement: null,
      focusedValue: null,
      isOpen: false
   })

   const { refetchInitiative } = useContext(InitiativeHierarchyContext)
   const { refetch: refetchOrganization } = useOrganization()

   const { removeUserFromRole } = useRemoveUserFromRole()
   const { resendRoleParticipationEmail } = useResendRoleParticipationEmail()
   const { userList, loading, fetchMore, refetch: refetchUsers } = useUserList({
      search: searchValue,
      userRelationshipFilter: {
         entityType: targetType,
         entityId: targetType === 'initiative'
            ? targetId
            : 'current',
         hasRoleParticipantElement: { roleId, status }
      },
      withRelationship: true,
      withRelationshipEntityType: targetType,
      withRelationshipEntityId: targetType === 'initiative'
         ? targetId
         : 'current'
   })

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: userList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               userList: {
                  ...prevResult.userList,
                  items: [
                     ...prevResult.userList.items,
                     ...fetchMoreResult.userList.items
                  ],
                  listInfo: fetchMoreResult.userList.listInfo
               }
            }
         }
      })
   }

   const handleRemoveUserFromRole = async (userId) => {
      const proceed = window.confirm('Are you sure you want to remove this user?')
      if (!proceed) {
         return
      }

      try {
         await removeUserFromRole({
            roleId,
            userId,
            entityType: targetType,
            entityId: targetType === 'initiative'
               ? targetId
               : 'current'
         })

         refetchUsers()

         if (targetType === 'initiative') {
            refetchInitiative()
         } else {
            refetchOrganization()
         }
      } catch (err) {
         window.alert(err.message || 'Could not remove user')
      }
   }

   const handleResendEmail = async (relationshipId) => {
      try {
         await resendRoleParticipationEmail({ relationshipId, roleId })
         window.alert('Email sent successfully')
      } catch (err) {
         window.alert(err.message || 'Could not send email')
      }
   }

   const handleClosePopover = () => {
      setPopoverConfig(config => ({
         ...config,
         isOpen: false,
         anchorElement: null
      }))
   }

   return (
      <View float="left" width="100%">
         <View
            float="left"
            width="100%"
            backgroundColor="#fff"
            style={{
               borderBottomLeftRadius: '6px',
               borderBottomRightRadius: '6px'
            }}>
            <View
               float="left"
               width="100%"
               padding="0px 12px">
               {!loading && userList.items.map((user, index) => {
                  const roleParticipantElement = user.relationship.elements.find(element => (
                     element.__typename === 'RelationshipElementRoleParticipant' &&
                     element.role.id === roleId
                  ))

                  return (
                     <UserListItem
                        key={user.id}
                        avatarMedia={user.avatarMedia}
                        banButtonLinkTo={`${match.url}/ban-user`}
                        fullName={user.fullName}
                        status={roleParticipantElement.status}
                        borderTop={index === 0 ? 'none' : undefined}
                        onOptionsButtonClick={(e) => {
                           setPopoverConfig({
                              isOpen: true,
                              anchorElement: e.currentTarget,
                              focusedValue: {
                                 ...user,
                                 status: roleParticipantElement.status
                              }
                           })
                        }}
                     />
                  )
               })}
               <Popover
                  anchorElement={popoverConfig.anchorElement}
                  anchorOriginHorizontal="right"
                  anchorOriginVertical="top"
                  width="180px"
                  onBodyClick={handleClosePopover}
                  open={popoverConfig.isOpen}>
                  {popoverConfig.focusedValue && (
                     <View
                        float="left"
                        width="100%"
                        borderRadius="6px"
                        backgroundColor="#fff"
                        boxShadow="6px 6px 30px rgba(0,0,0,0.1)">
                        <ListMenu buttonSize="sm">
                           <ListMenuButton
                              linkTo={`/profile/${popoverConfig.focusedValue.id}`}
                              name="View Profile"
                           />
                           <ListMenuButton
                              onClick={status === 'invited'
                                 ? () => {
                                    handleResendEmail(popoverConfig.focusedValue.relationship.id)
                                 }
                                 : () => {
                                    handleResendEmail(popoverConfig.focusedValue.relationship.id)
                                 }
                              }
                              name={`Resend ${status === 'active' ? 'Welcome' : 'Invite'} Email`}
                           />
                           {/* We can only ban active users */}
                           {popoverConfig.focusedValue.status === 'active' && (
                              <ListMenuButton
                                 onClick={() => setModalIsOpen(true)}
                                 name="Ban this user"
                              />
                           )}
                           <ListMenuButton
                              onClick={() => {
                                 handleRemoveUserFromRole(popoverConfig.focusedValue.id)
                              }}
                              name="Remove"
                           />
                        </ListMenu>
                     </View>
                  )}
               </Popover>
               {userList.items.length === 0 && (
                  <View padding="30px" textAlign="center">
                     <Text size="1.5" color="#888">
                        There are no users to show here
                     </Text>
                  </View>
               )}
            </View>
         </View>
         {userList.listInfo.hasNext && (
            <View
               float="left"
               width="100%"
               display="flex"
               justifyContent="center"
               padding="12px 0px">
               <Button
                  color="#000"
                  textColor="#fff"
                  size="xs"
                  onClick={handleLoadMore}
                  name="Load More"
                  mode={loading ? 'loading' : 'normal'}
                  rounded
               />
            </View>
         )}
         <Modal
            borderRadius="5px"
            width="100%[a-b] 400px[c-f]"
            maxHeight="100%"
            onCloseTrigger={() => setModalIsOpen(false)}
            open={modalIsOpen}>
            {/* We can only ban active users */}
            {popoverConfig.focusedValue?.status === 'active' && (
               <BanUserConfirm
                  onBanComplete={() => {
                     setModalIsOpen(false)
                     // TODO: this is a bit of a hack to get the list to refresh
                     // See PR #363
                     window.alert('User Ban successful')
                     window.location.assign(match.url)
                  }}
                  onCancelButtonClick={() => setModalIsOpen(false)}
                  userId={popoverConfig.focusedValue.id}
               />
            )}
         </Modal>
      </View>
   )
}

RoleUserList.propTypes = {
   roleId: PropTypes.string.isRequired,
   searchValue: PropTypes.string,
   status: PropTypes.string,
   targetId: PropTypes.string,
   targetType: PropTypes.oneOf(['organization', 'initiative']).isRequired
}

RoleUserList.defaultProps = {
   searchValue: null,
   status: 'active',
   targetId: undefined
}

export default React.memo(RoleUserList)
